import "../JSONTools/JSONformatter.css";
import ToolHeader from "../../Components/ToolHeader";
import "./TextUtilities.css";
import { Row, Col } from "react-bootstrap";
import { useState, useRef } from "react";
import { ColorPickerAPI } from "../../Services/APIService";



function clamp(value, min, max) {
    return Math.min(Math.max(value, min), max);
}

function getVariationIndicatorPosition(variations, selectedHsl) {
    const closestIndex = variations.reduce((closest, variation, index) => {
        const closestVariation = variations[closest];
        const distance =
            Math.abs(variation.h - selectedHsl.h) +
            Math.abs(variation.s - selectedHsl.s) +
            Math.abs(variation.l - selectedHsl.l);
        const closestDistance =
            Math.abs(closestVariation.h - selectedHsl.h) +
            Math.abs(closestVariation.s - selectedHsl.s) +
            Math.abs(closestVariation.l - selectedHsl.l);

        return distance < closestDistance ? index : closest;
    }, 0);

    return `${((closestIndex + 0.5) / variations.length) * 100}%`;
}

function normalizeNumberInput(value, min, max) {
    if (value === "") {
        return "";
    }

    return clamp(Number(value), min, max);
}

function parseRgbString(value) {
    const [r, g, b] = value.split(",").map((part) => Number(part.trim()));
    return { r, g, b };
}

function parseHslString(value) {
    const [h, s, l] = value
        .split(",")
        .map((part) => Number(part.trim().replace("%", "")));
    return { h, s, l };
}

function formatRgbInput(rgb) {
    return `${rgb.r},${rgb.g},${rgb.b}`;
}

function formatHslInput(hsl) {
    return `${hsl.h},${hsl.s}%,${hsl.l}%`;
}

function ColorPicker() {
    const toolFav = { toolName: 'Color Picker', Iconpath: "/FileCodeRaw.svg" };
    const [hex, setHex] = useState("#6DA7B3");
    const [rgb, setRgb] = useState({ r: 109, g: 167, b: 179 });
    const [hsl, setHsl] = useState({ h: 190, s: 32, l: 56 });
    const colorRequestId = useRef(0);








    const pickerRef = useRef(null);

    const applyColorResponse = (data) => {
        setHex(data.hex);
        setRgb(parseRgbString(data.rgb));
        setHsl(parseHslString(data.hsl));
    };

    const convertColor = async (inputFormat, input) => {
        const requestId = colorRequestId.current + 1;
        colorRequestId.current = requestId;

        try {
            const response = await ColorPickerAPI({
                inputFormat,
                input
            });

            if (requestId === colorRequestId.current) {
                applyColorResponse(response.data);
            }
        } catch (err) {
            console.log(err);
        }
    };

    const updatePicker = (e) => {

        console.log("Updating picker with:", {
            clientX: e.clientX,
            clientY: e.clientY
        });
        const rect =
            pickerRef.current.getBoundingClientRect();

        const x = clamp(
            e.clientX - rect.left,
            0,
            rect.width
        );

        const y = clamp(
            e.clientY - rect.top,
            0,
            rect.height
        );

        const saturation = Math.round(
            (x / rect.width) * 100
        );

        const lightness = Math.round(
            100 - (y / rect.height) * 100
        );

        const updatedHsl = {
            ...hsl,
            s: saturation,
            l: lightness
        };

        setHsl(updatedHsl);
        convertColor("HSL", formatHslInput(updatedHsl));
    };

    const startDrag = (e) => {
        updatePicker(e);

        const move = (ev) =>
            updatePicker(ev);

        const stop = () => {
            window.removeEventListener(
                "mousemove",
                move
            );
            window.removeEventListener(
                "mouseup",
                stop
            );
        };

        window.addEventListener(
            "mousemove",
            move
        );
        window.addEventListener(
            "mouseup",
            stop
        );
    };

    /* ---------------- HEX edit ---------------- */
    const handleHexChange = (value) => {
        colorRequestId.current += 1;
        setHex(value);

        const normalized = value.startsWith("#")
            ? value
            : `#${value}`;

        if (/^#[A-Fa-f0-9]{6}$/.test(normalized)) {
            convertColor("HEX", normalized);
        }
    }


    /* ---------------- RGB edit ---------------- */
    const handleRgbChange = (key, value) => {
        colorRequestId.current += 1;

        if (value !== "" && !/^\d+$/.test(value)) {
            return;
        }

        const updated = {
            ...rgb,
            [key]: normalizeNumberInput(value, 0, 255)
        };

        setRgb(updated);

        if (Object.values(updated).every((channel) => channel !== "")) {
            convertColor("RGB", formatRgbInput(updated));
        }
    };

    /* ---------------- HSL edit ---------------- */
    const handleHslChange = (
        key,
        value
    ) => {
        colorRequestId.current += 1;

        if (value !== "" && !/^\d+$/.test(value)) {
            return;
        }

        const max = key === "h" ? 360 : 100;
        const updated = {
            ...hsl,
            [key]: normalizeNumberInput(value, 0, max)
        };

        setHsl(updated);

        if (Object.values(updated).every((channel) => channel !== "")) {
            convertColor("HSL", formatHslInput(updated));
        }
    };

    const colorVariations = {
        tailwind: [95, 88, 82, 74, 66, 56, 48, 38, 28, 18].map((light) => ({
            h: hsl.h,
            s: hsl.s,
            l: light
        })),
        shades: [65, 58, 52, 45, 38, 30, 22, 14, 8, 3].map((light) => ({
            h: hsl.h,
            s: hsl.s,
            l: light
        })),
        tints: [92, 88, 84, 80, 76, 72, 68, 64, 60, 56].map((light) => ({
            h: hsl.h,
            s: hsl.s,
            l: light
        })),
        tones: [55, 54, 53, 52, 51, 50, 49, 48, 47, 46].map((light, i) => ({
            h: hsl.h,
            s: Math.max(hsl.s - i * 5, 5),
            l: light
        }))
    };

    return (
        <>
            <Col>
                <ToolHeader title="Color Picker" description="Pick a color and get HEX, RGB, and HSL values" toolFav={toolFav} />

                <Row className='Formatter color-picker-wrapper-row'>
                    <Col className="color-tool-column color-tool-container">
                        {/* TOP SECTION */}
                        <Row className="top-section color-picker-row">
                            {/* Left */}
                            <Col xs={12} lg={8} md={8} className="color-picker-main-column">
                                <div className="picker-card">
                                    <div
                                        ref={pickerRef}
                                        className="color-picker-box"
                                        style={{
                                            background: `hsl(${hsl.h},100%,50%)`
                                        }}
                                        onMouseDown={startDrag}
                                    >
                                        <div className="white-overlay" />
                                        <div className="black-overlay" />

                                        <div
                                            className="picker-dot"
                                            style={{
                                                left: `${hsl.s}%`,
                                                top: `${100 - hsl.l}%`
                                            }}
                                        />
                                    </div>

                                    {/* Hue Slider */}
                                    <input
                                        type="range"
                                        min="0"
                                        max="360"
                                        value={hsl.h}
                                        className="hue-slider"
                                        onChange={(e) => {
                                            const updatedHsl = {
                                                ...hsl,
                                                h: Number(
                                                    e.target.value
                                                )
                                            };

                                            setHsl(updatedHsl);
                                            convertColor("HSL", formatHslInput(updatedHsl));
                                        }}
                                    />

                                    <div className="preview-section">
                                        <div
                                            className="preview-box"
                                            style={{
                                                background: hex
                                            }}
                                        />
                                        <div>
                                            <h4>{hex}</h4>
                                            <p>
                                                rgb({rgb.r},{rgb.g},
                                                {rgb.b})
                                            </p>
                                            <p>
                                                hsl({hsl.h},
                                                {hsl.s}%,
                                                {hsl.l}%)
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </Col>

                            {/* Right */}
                            <Col xs={12} lg={4} md={4} className="color-picker-side-column">
                                <div className="input-section">
                                    <div className="input-card">
                                        <label>HEX</label>
                                        <input
                                            value={hex}
                                            onChange={(e) =>
                                                handleHexChange(
                                                    e.target.value
                                                )
                                            }
                                        />
                                    </div>

                                    <div className="input-card">
                                        <label>RGB</label>
                                        <div className="row-inputs">
                                            <input
                                                value={rgb.r}
                                                onChange={(e) =>
                                                    handleRgbChange(
                                                        "r",
                                                        e.target.value
                                                    )
                                                }
                                            />
                                            <input
                                                value={rgb.g}
                                                onChange={(e) =>
                                                    handleRgbChange(
                                                        "g",
                                                        e.target.value
                                                    )
                                                }
                                            />
                                            <input
                                                value={rgb.b}
                                                onChange={(e) =>
                                                    handleRgbChange(
                                                        "b",
                                                        e.target.value
                                                    )
                                                }
                                            />
                                        </div>
                                    </div>

                                    <div className="input-card">
                                        <label>HSL</label>
                                        <div className="row-inputs">
                                            <input
                                                value={hsl.h}
                                                onChange={(e) =>
                                                    handleHslChange(
                                                        "h",
                                                        e.target.value
                                                    )
                                                }
                                            />
                                            <input
                                                value={hsl.s}
                                                onChange={(e) =>
                                                    handleHslChange(
                                                        "s",
                                                        e.target.value
                                                    )
                                                }
                                            />
                                            <input
                                                value={hsl.l}
                                                onChange={(e) =>
                                                    handleHslChange(
                                                        "l",
                                                        e.target.value
                                                    )
                                                }
                                            />
                                        </div>
                                    </div>
                                </div>
                            </Col>
                        </Row>

                        {/* Variations */}
                        {/* Color Variations */}
                        <h3 className="color-header">Color Variations</h3>
                        <div className="variation-card">


                            {/* Tailwind */}
                            <div className="variation-group">
                                <label>Tailwind</label>
                                <div className="variation-strip">
                                    {colorVariations.tailwind.map((variation, i) => (
                                        <div
                                            key={i}
                                            className="variation-box"
                                            style={{
                                                background: `hsl(${variation.h}, ${variation.s}%, ${variation.l}%)`
                                            }}
                                        />
                                    ))}

                                    <div
                                        className="variation-indicator"
                                        style={{
                                            left: getVariationIndicatorPosition(colorVariations.tailwind, hsl)
                                        }}
                                    />
                                </div>
                            </div>

                            {/* Shades */}
                            <div className="variation-group">
                                <label>Shades</label>
                                <div className="variation-strip">
                                    {colorVariations.shades.map((variation, i) => (
                                        <div
                                            key={i}
                                            className="variation-box"
                                            style={{
                                                background: `hsl(${variation.h}, ${variation.s}%, ${variation.l}%)`
                                            }}
                                        />
                                    ))}

                                    <div
                                        className="variation-indicator"
                                        style={{
                                            left: getVariationIndicatorPosition(colorVariations.shades, hsl)
                                        }}
                                    />
                                </div>
                            </div>

                            {/* Tints */}
                            <div className="variation-group">
                                <label>Tints</label>
                                <div className="variation-strip">
                                    {colorVariations.tints.map((variation, i) => (
                                        <div
                                            key={i}
                                            className="variation-box"
                                            style={{
                                                background: `hsl(${variation.h}, ${variation.s}%, ${variation.l}%)`
                                            }}
                                        />
                                    ))}

                                    <div
                                        className="variation-indicator"
                                        style={{
                                            left: getVariationIndicatorPosition(colorVariations.tints, hsl)
                                        }}
                                    />
                                </div>
                            </div>

                            {/* Tones */}
                            <div className="variation-group">
                                <label>Tones</label>
                                <div className="variation-strip">
                                    {colorVariations.tones.map((variation, i) => (
                                        <div
                                            key={i}
                                            className="variation-box"
                                            style={{
                                                background: `hsl(${variation.h}, ${variation.s}%, ${variation.l}%)`
                                            }}
                                        />
                                    ))}

                                    <div
                                        className="variation-indicator"
                                        style={{
                                            left: getVariationIndicatorPosition(colorVariations.tones, hsl)
                                        }}
                                    />
                                </div>
                            </div>
                        </div>
                        {/* Conversion Table */}
                        <h3 className="color-header">Color Conversion</h3>
                        <div className="conversion-card">

                            <table>
                                <tbody>
                                    <tr>
                                        <td>HEX</td>
                                        <td>{hex}</td>
                                    </tr>
                                    <tr>
                                        <td>RGB</td>
                                        <td>
                                            {rgb.r}, {rgb.g},{" "}
                                            {rgb.b}
                                        </td>
                                    </tr>
                                    <tr>
                                        <td>HSL</td>
                                        <td>
                                            {hsl.h},{" "}
                                            {hsl.s}%,
                                            {hsl.l}%
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </Col>
                </Row>
            </Col >
        </>
    )
}

export default ColorPicker;
